import { useEffect, useState } from 'react';
import { useTranslate, useUpdate } from '@refinedev/core';
import { SaveButton } from '@refinedev/antd';
import { Card, Flex, Form, Space, Tag, Typography } from 'antd';
import { OrderedSectionCardsField } from '@/components/user_catalog/OrderedSectionCardsField';
import { orderedRowIds } from '@/components/user_catalog/orderedRows';
import {
  normalizeSectionRows,
  type SectionRow,
} from '@/components/user_catalog/sections/sectionRows';
import { sectionsDraftFromForm, type SectionsDraft } from '../homeDraft';

type SectionsForm = { sections: SectionRow[] };

interface SectionsTabProps {
  rootId: number;
  /** Server rows in display order; row `id`s ride along so the save updates
   * instead of recreating. Seeds the form once, on mount. */
  sections: SectionRow[];
  dirty: boolean;
  onDraftChange: (draft: SectionsDraft) => void;
}

export const SectionsTab = ({
  rootId,
  sections,
  dirty,
  onDraftChange,
}: SectionsTabProps) => {
  const t = useTranslate();
  const [form] = Form.useForm<SectionsForm>();
  const [saving, setSaving] = useState(false);
  const { mutateAsync: patchCategory } = useUpdate();

  const values = Form.useWatch<SectionsForm | undefined>([], form);
  // Undefined until the store initialises — reporting it would blank the
  // shell's draft.
  useEffect(() => {
    if (values) onDraftChange(sectionsDraftFromForm(values));
  }, [values, onDraftChange]);

  const pickedCount = orderedRowIds(
    values?.sections ?? sections,
    'collection_id'
  ).length;

  const handleFinish = async (formValues: SectionsForm) => {
    setSaving(true);
    try {
      await patchCategory({
        resource: 'user_catalog.categories',
        id: rootId,
        values: { sections: normalizeSectionRows(formValues.sections) },
        meta: { method: 'patch' },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      form={form}
      initialValues={{ sections }}
      onFinish={handleFinish}
      layout="vertical"
    >
      <Flex vertical gap="middle">
        <Card
          size="small"
          title={t('user_catalog.home_page.cards.sections')}
          extra={<Tag>{pickedCount}</Tag>}
        >
          <Flex vertical gap="small">
            <Typography.Text type="secondary">
              {t('user_catalog.home_page.sections_hint')}
            </Typography.Text>
            <OrderedSectionCardsField
              categoryId={rootId}
              existingCollectionIds={orderedRowIds(sections, 'collection_id')}
            />
          </Flex>
        </Card>

        <Space>
          <SaveButton htmlType="submit" loading={saving} />
          {dirty && (
            <Tag color="warning">{t('user_catalog.home_page.unsaved')}</Tag>
          )}
        </Space>
      </Flex>
    </Form>
  );
};
